import {useCallback, useMemo} from 'react';

import {ComparisonViewDefinition} from '../wfReactInterface/generatedBuiltinObjectClasses.zod';
import {
  TraceObjSchemaForBaseObjectClass,
  useBaseObjectInstances,
  useCreateBuiltinObjectInstance,
} from '../wfReactInterface/objectClassQuery';
import {projectIdFromParts} from '../wfReactInterface/tsDataModelHooks';

/**
 * Generates a unique ID for a new comparison view based on the current timestamp.
 * Format: comparison_YYYY-MM-DD_HH-MM-SS-mmm
 */
export const getNewComparisonViewId = (): string => {
  const now = new Date();
  return `comparison_${now
    .toISOString()
    .replace('T', '_')
    .replace(/[:.]/g, '-')
    .slice(0, -1)}`;
};

/**
 * Generates a user-friendly default label for a new comparison view based on the current date and time.
 * Format: "Comparison YYYY-MM-DD HH:MM:SS"
 *
 * @example
 * getDefaultComparisonViewLabel() // => "Comparison 2025-01-31 14:30:45"
 */
export const getDefaultComparisonViewLabel = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `Comparison ${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Removes query parameters from a URL, returning only the base path.
 */
export const getBaseUrlWithoutParams = (url: string): string => {
  return url.split('?')[0];
};

/**
 * Constructs a view URL by combining a base URL with a view ID query parameter.
 */
export const buildViewUrl = (baseUrl: string, viewId: string): string => {
  return `${getBaseUrlWithoutParams(baseUrl)}?view=${encodeURIComponent(viewId)}`;
};

type ComparisonViewInstances = {
  loading: boolean;
  views: Array<TraceObjSchemaForBaseObjectClass<'ComparisonView'>>;
  refetchViews: () => Promise<void>;
};

export const useComparisonViewInstances = (
  projectId: string
): ComparisonViewInstances => {
  const comparisonViewQuery = useBaseObjectInstances('ComparisonView', {
    project_id: projectId,
    filter: {latest_only: true},
  });

  const views = useMemo(() => {
    return comparisonViewQuery.result ?? [];
  }, [comparisonViewQuery.result]);

  const refetchViews = async () => {
    await comparisonViewQuery.refetch();
  };

  return {
    loading: comparisonViewQuery.loading,
    views,
    refetchViews,
  };
};

export const useCreateComparisonView = (entity: string, project: string) => {
  const createComparisonViewInstance =
    useCreateBuiltinObjectInstance('ComparisonView');

  const createComparisonView = useCallback(
    (objectId: string, label: string, definition: ComparisonViewDefinition) => {
      return createComparisonViewInstance({
        obj: {
          project_id: projectIdFromParts({entity, project}),
          object_id: objectId,
          val: {
            label,
            definition,
          },
        },
      });
    },
    [createComparisonViewInstance, entity, project]
  );

  return createComparisonView;
};

export const comparisonViewDefinitionToParams = (
  definition: ComparisonViewDefinition
): Record<string, any> => {
  const params: Record<string, any> = {};

  if (definition.evaluation_call_ids) {
    params.evaluationCallIds = definition.evaluation_call_ids;
  }

  if (definition.selected_metrics) {
    params.metrics = definition.selected_metrics;
  }

  return params;
};

/**
 * Converts metrics from list format (used in API) to dict format (used internally).
 * @param metrics - Array of metric names (selected metrics)
 * @returns Object mapping metric names to true (selected) or null if no metrics
 *
 * @example
 * metricsListToDict(['accuracy', 'f1']) // => {accuracy: true, f1: true}
 * metricsListToDict([]) // => {} (all hidden)
 * metricsListToDict(null) // => null
 */
export const metricsListToDict = (
  metrics: string[] | null | undefined
): Record<string, boolean> | null => {
  if (metrics === null || metrics === undefined) return null;
  if (metrics.length === 0) return {};
  return Object.fromEntries(metrics.map(m => [m, true]));
};

/**
 * Converts metrics from dict format (used internally) to list format (used in API).
 * @param metrics - Object mapping metric names to their selected state
 * @returns Array of selected metric names or null if no metrics
 *
 * @example
 * metricsDictToList({accuracy: true, f1: false}) // => ['accuracy']
 * metricsDictToList({}) // => []
 * metricsDictToList(null) // => null
 */
export const metricsDictToList = (
  metrics: Record<string, boolean> | null | undefined
): string[] | null => {
  if (!metrics) return null;
  const selected = Object.entries(metrics)
    .filter(([_, v]) => v === true)
    .map(([k]) => k);
  return selected;
};

export const urlParamsToComparisonViewDefinition = (urlParams: {
  evaluationCallIds?: string[];
  metrics?: Record<string, boolean> | null;
}): ComparisonViewDefinition => {
  const definition: ComparisonViewDefinition = {
    evaluation_call_ids: urlParams.evaluationCallIds ?? [],
    selected_metrics: metricsDictToList(urlParams.metrics) ?? undefined,
  };

  return definition;
};
